import React, { useMemo } from "react";
import { format, differenceInDays } from "date-fns";
import { CheckCircle2 } from "lucide-react";

function daysSince(dateStr) {
  if (!dateStr) return null;
  return differenceInDays(new Date(), new Date(dateStr));
}

export default function SweepStalenessTable({ races, stalenessThreshold, isOverdue, onRaceClick }) {
  const overdueRaces = useMemo(() => {
    return races
      .filter((r) => isOverdue(r.last_digital_sweep) || isOverdue(r.last_linear_sweep))
      .map((r) => {
        const digDays = daysSince(r.last_digital_sweep);
        const linDays = daysSince(r.last_linear_sweep);
        const digOverdue = isOverdue(r.last_digital_sweep);
        const linOverdue = isOverdue(r.last_linear_sweep);
        return { ...r, digDays, linDays, digOverdue, linOverdue, bothOverdue: digOverdue && linOverdue };
      })
      .sort((a, b) => {
        if (a.bothOverdue !== b.bothOverdue) return a.bothOverdue ? -1 : 1;
        const aMax = Math.max(a.digDays ?? 9999, a.linDays ?? 9999);
        const bMax = Math.max(b.digDays ?? 9999, b.linDays ?? 9999);
        return bMax - aMax;
      });
  }, [races, isOverdue]);

  if (overdueRaces.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <p className="text-sm font-medium text-emerald-700">All sweeps are current.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border">
        <p className="text-sm font-semibold">Sweep Staleness Detail</p>
        <p className="text-xs text-muted-foreground mt-0.5">{overdueRaces.length} race{overdueRaces.length !== 1 ? "s" : ""} with overdue sweep — click a row to open race details</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Advertiser</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Race</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Analyst</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Last Digital Sweep</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Last Linear Sweep</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Days (Dig.)</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Days (Lin.)</th>
            </tr>
          </thead>
          <tbody>
            {overdueRaces.map((r, i) => (
              <tr
                key={r.id}
                className={`border-b border-border cursor-pointer hover:bg-accent/50 transition-colors ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                onClick={() => onRaceClick(r)}
              >
                <td className="px-4 py-2.5 font-medium max-w-[140px] truncate">{r._ad?.advertiser_name}</td>
                <td className="px-4 py-2.5 text-muted-foreground font-mono whitespace-nowrap">
                  {r.state} · {r.chamber}{r.district ? ` · ${r.district}` : ""}
                </td>
                <td className="px-4 py-2.5">{r._ad?.data_analyst || "—"}</td>
                <td className={`px-4 py-2.5 whitespace-nowrap font-medium ${r.digOverdue ? "text-red-600" : "text-muted-foreground"}`}>
                  {r.last_digital_sweep ? format(new Date(r.last_digital_sweep), "MMM d, yyyy") : "Never"}
                </td>
                <td className={`px-4 py-2.5 whitespace-nowrap font-medium ${r.linOverdue ? "text-red-600" : "text-muted-foreground"}`}>
                  {r.last_linear_sweep ? format(new Date(r.last_linear_sweep), "MMM d, yyyy") : "Never"}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.digOverdue ? "text-red-600" : "text-muted-foreground"}`}>
                  {r.digDays !== null ? r.digDays : "—"}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.linOverdue ? "text-red-600" : "text-muted-foreground"}`}>
                  {r.linDays !== null ? r.linDays : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}